/**
 * Unified API Client
 * Single point of communication with monolithic backend
 * Replaces multiple microservice endpoints
 */

import axios from 'axios';
import tokenManager from './auth/tokenManager';
import {
  getCurrentOrgSlug,
  getCurrentBranchSlug,
  getCurrentClientSlug,
  buildStateAwarePath,
  stripStatePrefix,
} from '../utils/tenant';

// Pre-request freshness guard — SessionManager registers itself here to avoid circular import
let _preRequestGuard = null;
export const registerPreRequestGuard = (guardFn) => {
  _preRequestGuard = guardFn;
};

// Post-refresh callback — SessionManager registers to restart its cookie timer
let _onRefreshSuccess = null;
export const registerOnRefreshSuccess = (callback) => {
  _onRefreshSuccess = callback;
};

let _unauthorizedRecovery = null;
export const registerUnauthorizedRecovery = (callback) => {
  _unauthorizedRecovery = callback;
};

const API_TIMEOUT = import.meta.env.VITE_API_TIMEOUT || 30000;
const RAW_API_BASE = (import.meta.env.VITE_API_BASE_URL || '').trim();
const HOST_MODE = (import.meta.env.VITE_API_HOST_MODE || 'static').toLowerCase();
const API_PORT = Number(import.meta.env.VITE_API_PORT || 8001);
const API_PATH = (import.meta.env.VITE_API_BASE_PATH || '/api/v1').trim() || '/api/v1';
const NORMALIZED_API_PATH = API_PATH.startsWith('/') ? API_PATH : `/${API_PATH}`;
const resolveApiEndpoint = () => {
  const fallbackUrl = `http://localhost:${API_PORT}${NORMALIZED_API_PATH}`;

  // When served over HTTPS in dev, use the current origin so requests go
  // through the Vite proxy and avoid mixed-content blocking.
  if (typeof window !== 'undefined' && window.location?.protocol === 'https:' && !import.meta.env.PROD) {
    return {
      origin: window.location.origin,
      path: NORMALIZED_API_PATH,
    };
  }

  if (HOST_MODE === 'tenant' && typeof window !== 'undefined') {
    const protocol = window.location?.protocol || 'http:';
    const hostname = window.location?.hostname || 'localhost';
    const portSegment = API_PORT ? `:${API_PORT}` : '';
    return {
      origin: `${protocol}//${hostname}${portSegment}`,
      path: NORMALIZED_API_PATH,
    };
  }

  const candidate = RAW_API_BASE || fallbackUrl;
  let parsed;
  try {
    parsed = new URL(candidate);
  } catch (error) {
    parsed = new URL(fallbackUrl);
  }

    const cleanedPath = (parsed.pathname || '').replace(/\/+$/g, '');
  return {
    origin: `${parsed.protocol}//${parsed.host}`,
    path: cleanedPath || NORMALIZED_API_PATH,
  };
};

const { origin: API_ORIGIN, path: API_PATH_PREFIX } = resolveApiEndpoint();

if (!import.meta.env.PROD) {
  console.log(`[API Client] API origin (${HOST_MODE} mode):`, API_ORIGIN, 'prefix:', API_PATH_PREFIX);
}

// Create axios instance with credentials for cookie-based auth
const apiClient = axios.create({
  baseURL: API_ORIGIN,
  timeout: API_TIMEOUT,
  withCredentials: true, // CRITICAL: Include cookies in cross-origin requests
  maxBodyLength: Infinity,    // Allow large file uploads (bulk doc can be 150MB+)
  maxContentLength: Infinity,
  headers: {
    'Content-Type': 'application/json',
  },
});

const PUBLIC_ENDPOINT_PATTERNS = [
  /^\/invitations\//,
  /^\/b2b\/invitations\//,
  /^\/auth\/magic-link/,
  /^\/auth\/check-membership/,
  /^\/auth\/discovery\//,
  /^\/auth\/session\/exchange/,
  /^\/platform\/session\/exchange/,
  /^\/auth\/session\/verify/
];

const API_BASE_PATH = (API_PATH_PREFIX || '').replace(/\/*$/, '');

// Export for direct fetch calls (e.g., bulk upload) that bypass axios
export const getApiBaseUrl = () => `${API_ORIGIN}${API_BASE_PATH}`;

const normalizeUrlPath = (url) => {
  if (!url) return '/';
  // Ensure fallbackBase has trailing slash so relative URLs resolve correctly
  // Without trailing slash: new URL('foo', 'http://x/api/v1') => /api/foo (wrong)
  // With trailing slash: new URL('foo', 'http://x/api/v1/') => /api/v1/foo (correct)
  const basePath = API_BASE_PATH || '';
  const fallbackBase = `${API_ORIGIN}${basePath}${basePath ? '/' : ''}` || 'http://localhost/';
  try {
    const parsed = new URL(url, fallbackBase);
    let path = parsed.pathname || '';
    if (basePath && path.startsWith(basePath)) {
      path = path.slice(basePath.length);
      if (path && !path.startsWith('/')) {
        path = `/${path}`;
      }
    }
    return path || '/';
  } catch (error) {
    try {
      const fallback = new URL(url, 'http://localhost/');
      let path = fallback.pathname || '';
      if (basePath && path.startsWith(basePath)) {
        path = path.slice(basePath.length);
        if (path && !path.startsWith('/')) {
          path = `/${path}`;
        }
      }
      return path || '/';
    } catch (err) {
      return url.startsWith('/') ? url : `/${url}`;
    }
  }
};

const isPublicEndpointPath = (path) =>
  PUBLIC_ENDPOINT_PATTERNS.some((pattern) => pattern.test(path));

const buildApiPath = (relativePath = '/') => {
  const normalized = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
  const basePath = API_BASE_PATH || '';
  const combined = `${basePath}${normalized}`.replace(/\/+/g, '/');
  return combined.startsWith('/') ? combined : `/${combined}`;
};

// Track refresh attempts to prevent infinite loops
let isRefreshing = false;
let refreshSubscribers = [];

// Subscribe to token refresh
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Notify all subscribers when token is refreshed
const onTokenRefreshed = (result) => {
  refreshSubscribers.forEach((callback) => callback(result));
  refreshSubscribers = [];
};

// Request interceptor to add auth token and other headers
apiClient.interceptors.request.use(
  async (config) => {
    // Pre-request: ensure token is fresh before sending (prevents 401 during slow ops)
    const isRefreshEndpoint = config.url?.includes('/auth/session/refresh');
    if (!isRefreshEndpoint && !config._retry && _preRequestGuard) {
      try {
        await _preRequestGuard();
      } catch (_e) {
        // Continue anyway — the 401 handler will catch it
      }
    }

    // FormData uploads: remove Content-Type so axios auto-sets
    // multipart/form-data with the correct boundary string.
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
      // Also clear from axios common/method defaults
      if (config.headers.common) delete config.headers.common['Content-Type'];
      if (config.headers.post) delete config.headers.post['Content-Type'];
      if (config.headers.put) delete config.headers.put['Content-Type'];
      if (config.headers.patch) delete config.headers.patch['Content-Type'];
    }

    // For session-based endpoints, don't add Authorization header
    // as they expect session_token in the request body
    const isSessionEndpoint = config.url?.includes('/auth/session/');
    const requestPath = normalizeUrlPath(config.url);
    const isPublicEndpoint = isPublicEndpointPath(requestPath);
    
    // Debug logging for platform endpoints
    const isPlatformEndpoint = config.url?.includes('/platform/');

    if (isPublicEndpoint) {
      delete config.headers.Authorization;
      delete config.headers['Authorization'];
      delete config.headers['X-Organization-Id'];
      delete config.headers['x-organization-id'];
    }
    if (isPlatformEndpoint && import.meta.env.DEV) {
      console.log('[API Client] Platform endpoint detected:', config.url);
    }
    
    if (!isSessionEndpoint && !isPublicEndpoint) {
      // Cookie-only authentication mode
      // NO Bearer tokens - authentication is handled via HttpOnly cookies
      // The withCredentials: true setting ensures cookies are sent automatically

      // Enhanced debug logging for platform endpoints
      if (isPlatformEndpoint && import.meta.env.DEV) {
        console.log('[API Client] Platform endpoint detected (cookie-auth):', config.url);
        console.log('[API Client] Using cookie-based authentication');
        console.log('[API Client] withCredentials:', config.withCredentials);
      }
    }

    // DO NOT add country header for platform or auth endpoints
    // The X-Country-Code header is not configured in backend CORS
    // and causes preflight failures
    // Comment out for now until backend CORS is updated
    /*
    if (isPlatformEndpoint) {
      const country = import.meta.env.VITE_DEFAULT_COUNTRY || 'US';
      config.headers['X-Country-Code'] = country;
    }
    */

    // Final debug log for platform endpoints (never log header values)
    if (isPlatformEndpoint && import.meta.env.DEV) {
      console.log('[API Client] Final request config:', {
        url: config.url,
        method: config.method,
        withCredentials: config.withCredentials
      });
    }

    let search = '';
    try {
      const parsedForSearch = new URL(
        config.url || '',
        `${API_ORIGIN}${API_BASE_PATH || ''}` || 'http://localhost'
      );
      search = parsedForSearch.search || '';
    } catch (error) {
      search = '';
    }

    const fullApiPath = buildApiPath(requestPath || '/');
    // API URLs should NOT have state prefixes - only frontend routes use state routing
    config.url = `${fullApiPath}${search}`;

    // Add organization context if available
    const userData = tokenManager.getUserData();
    if (!isPublicEndpoint) {
      const organizationId = userData?.organizationId || userData?.organization_id;
      if (organizationId) {
        config.headers['X-Organization-Id'] = organizationId;
      }
    }

    const clientSlugFromHost = getCurrentClientSlug();
    const branchSlugFromUser = userData?.branch_slug || userData?.branchSlug || userData?.branch?.slug;
    const clientSlugFromUser = userData?.client_organization_slug || userData?.clientOrganizationSlug;

    let organizationSlug =
      clientSlugFromUser ||
      userData?.organization_slug ||
      clientSlugFromHost ||
      getCurrentOrgSlug();

    if (
      organizationSlug &&
      branchSlugFromUser &&
      organizationSlug === branchSlugFromUser &&
      clientSlugFromUser
    ) {
      organizationSlug = clientSlugFromUser;
    }

    if (organizationSlug) {
      config.headers['X-Tenant-Slug'] = organizationSlug;
    }

    const branchSlug = branchSlugFromUser || getCurrentBranchSlug();
    if (branchSlug) {
      config.headers['X-Branch-Slug'] = branchSlug;
    }

    if (typeof window !== 'undefined' && window.location?.hostname) {
      config.headers['X-Tenant-Host'] = window.location.hostname;
    }

    if (!import.meta.env.PROD) {
      console.log('[API Client] Tenant headers prepared', {
        path: requestPath,
        organizationSlug,
        branchSlug,
        tenantHost: config.headers['X-Tenant-Host']
      });
    }

    // Log request in debug mode
    if (import.meta.env.VITE_ENABLE_DEBUG === 'true') {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`, config.data);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Perform logout redirect after failed auth recovery.
 * Extracted to avoid duplication between 401 handler paths.
 * Guarded to prevent multiple concurrent redirects.
 */
let _isRedirecting = false;
const performLogoutRedirect = (sessionRevokeReason) => {
  if (_isRedirecting) return;
  _isRedirecting = true;

  tokenManager.clearUserData();
  const tenantSlug = (window.location.hostname.split('.')[0] || 'default').toLowerCase();
  localStorage.removeItem(`mira_session_metadata_${tenantSlug}`);
  localStorage.removeItem(`mira_session_refresh_leader_${tenantSlug}`);
  // Legacy cleanup
  localStorage.removeItem('mira_session_metadata');
  localStorage.removeItem('mira_session_refresh_leader');

  // Stop SessionManager timers to prevent further refresh/retry calls
  try {
    const sessionManager = require('./session/SessionManager').default;
    if (sessionManager && typeof sessionManager.cleanup === 'function') {
      sessionManager.cleanup();
    }
  } catch (_e) { /* non-critical */ }

  if (window.dispatchEvent) {
    window.dispatchEvent(new CustomEvent('sessionExpired', {
      detail: { reason: 'unauthorized' }
    }));
  }

  const authPaths = [
    '/login/b2b', '/login/consumer', '/login', '/auth/callback',
    '/platform/login', '/platform/auth/callback',
    '/register', '/reset-password', '/verify-email',
    '/onboarding',
  ];

  const currentPath = stripStatePrefix(window.location.pathname);
  const isAuthPage = authPaths.some(path => currentPath.startsWith(path));

  if (!isAuthPage) {
    // Detect B2C consumer by cached user role or by checking if the user
    // was on a consumer-only page (pipeline, profile, documents, etc.)
    const cachedUser = tokenManager.getUserData();
    const userRole = (cachedUser?.role || cachedUser?.originalRole || '').toLowerCase();
    const isConsumer =
      userRole.includes('consumer') ||
      currentPath.includes('/consumer') ||
      currentPath.includes('/b2c') ||
      currentPath.startsWith('/pipeline') ||
      currentPath.startsWith('/profile') ||
      currentPath.startsWith('/documents');

    let redirectUrl = buildStateAwarePath('/login/b2b');
    if (currentPath.startsWith('/platform')) {
      redirectUrl = '/platform/login';
    } else if (isConsumer) {
      redirectUrl = buildStateAwarePath('/login/consumer');
    }
    if (sessionRevokeReason) {
      redirectUrl += '?reason=revoked';
    }
    window.location.replace(redirectUrl);
  }
};

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response) => {
    // Log response in debug mode
    if (import.meta.env.VITE_ENABLE_DEBUG === 'true') {
      console.log(`API Response: ${response.config.url}`, response.data);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    // CRITICAL FIX: Don't retry auth endpoints — they cause infinite 401 loops
    const requestUrl = originalRequest.url || '';
    const isAuthEndpoint =
      requestUrl.includes('/auth/session/exchange') ||
      requestUrl.includes('/auth/session/refresh') ||
      requestUrl.includes('/auth/session/revoke') ||
      requestUrl.includes('/auth/me') ||
      requestUrl.includes('/auth/discovery');

    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;

      // Check if session was truly revoked — only skip refresh for terminal reasons
      const errorDetail = error.response?.data?.detail;
      const errorReason = errorDetail && typeof errorDetail === 'object' ? errorDetail.error : null;
      const isDefinitiveFailure = errorReason === 'session_revoked';

      if (isDefinitiveFailure) {
        console.log('[API Client] Session definitively revoked:', errorReason);
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('mira_auth:last_revoked_reason', errorDetail.reason || 'concurrent_login');
        }
        // Skip refresh — go straight to logout
        performLogoutRedirect(errorReason);
        return Promise.reject(error);
      }

      // Attempt to refresh the session cookie before logging out
      console.log('[API Client] Auth failure received — attempting session recovery');

      try {
        // Coalesce concurrent auth recovery inside the tab.
        if (!isRefreshing) {
          isRefreshing = true;
          refreshSubscribers = [];
          const recoveryCall = _unauthorizedRecovery
            ? _unauthorizedRecovery({
                error,
                originalRequest,
              })
            : Promise.resolve({ recovered: false, classification: 'definitive', reason: 'missing_recovery_handler' });

          recoveryCall
            .then((result) => {
              onTokenRefreshed(result);
              if (result?.recovered && _onRefreshSuccess) {
                _onRefreshSuccess(result?.payload || null);
              }
            })
            .catch((recoveryError) => {
              console.error('[API Client] Session recovery failed:', recoveryError);
              onTokenRefreshed({
                recovered: false,
                classification: 'transient',
                reason: recoveryError?.message || 'recovery_failed',
                error: recoveryError,
              });
            })
            .finally(() => {
              isRefreshing = false;
            });

          const recoveryResult = await recoveryCall;
          if (recoveryResult?.recovered) {
            return apiClient(originalRequest);
          }
          if (recoveryResult?.classification === 'transient') {
            return Promise.reject(error);
          }
          throw recoveryResult || error;
        } else {
          // Another auth recovery is already running in this tab — wait for it.
          await new Promise((resolve, reject) => {
            subscribeTokenRefresh((result) => {
              if (result?.recovered) {
                resolve(result);
                return;
              }
              if (result?.classification === 'transient') {
                reject(result);
                return;
              }
              reject(result || error);
            });
          });
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        if (refreshError?.classification === 'transient') {
          console.log('[API Client] Session recovery degraded, preserving local auth');
          return Promise.reject(error);
        }
        console.log('[API Client] Session recovery definitive failure, logging out');
        performLogoutRedirect(refreshError?.reason || null);
        return Promise.reject(error);
      }
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      console.error('Access forbidden:', error.response.data);
      // Could redirect to unauthorized page
    }

    // Handle 404 Not Found
    if (error.response?.status === 404) {
      console.error('Resource not found:', error.response.data);
    }

    // Handle 500 Server Error
    if (error.response?.status >= 500) {
      console.error('Server error:', error.response.data);
    }

    // Handle network errors
    if (!error.response) {
      console.error('Network error:', error.message);
      error.message = 'Network error. Please check your connection.';
    }

    // Log error in debug mode
    if (import.meta.env.VITE_ENABLE_DEBUG === 'true') {
      console.error('API Error:', error.response || error);
    }

    return Promise.reject(error);
  }
);

// Helper methods for common HTTP operations
const api = {
  // GET request
  get: (url, config = {}) => apiClient.get(url, config),

  // POST request
  post: (url, data = {}, config = {}) => apiClient.post(url, data, config),

  // PUT request
  put: (url, data = {}, config = {}) => apiClient.put(url, data, config),

  // PATCH request
  patch: (url, data = {}, config = {}) => apiClient.patch(url, data, config),

  // DELETE request
  delete: (url, config = {}) => apiClient.delete(url, config),

  // File upload with multipart/form-data
  upload: (url, formData, onProgress) => {
    return apiClient.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onProgress,
    });
  },

  // File download
  download: (url, filename) => {
    return apiClient.get(url, {
      responseType: 'blob',
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    });
  },

  // Logout - call backend logout endpoint
  logout: async () => {
    try {
      const refreshToken = tokenManager.getRefreshToken();
      if (refreshToken) {
        // Call the new logout endpoint from Alex's implementation
        await apiClient.post('/auth/logout', {
          refresh_token: refreshToken
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local tokens
      tokenManager.clearToken();
    }
  },
};

export default api;

// Export the raw axios instance for callers that need the generic
// axios(config) calling convention (e.g. RTK Query baseQuery).
export { apiClient as axiosInstance };
